import * as dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { Component, ReactNode } from 'react';
import DatePicker from 'react-date-picker';
import { NavLink, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import { AdminApi } from '../admin-api';
import Header from './Header';
import PageLoading from './PageLoading';
import Sidebar from './Sidebar';

import 'react-calendar/dist/Calendar.css';
import 'react-date-picker/dist/DatePicker.css';

export default function Expenses(props: any): ReactNode {
  const [searchParams] = useSearchParams();

  return (
    <ExpensesComp
      navigate={useNavigate()}
      {...props}
      searchParams={searchParams}
    />
  );
}

class ExpensesComp extends Component {
  constructor(
    readonly props: Readonly<{
      children?: ReactNode;
      navigate?: any;
      currentUser?: any;
      searchParams?: URLSearchParams;
    }>,
  ) {
    super(props);
    this.state = {
      isLoading: true,
      list: [],
      allCategories: [],
      selectedCategories: [],
      showDone: false,
      showUndone: false,
      minDate: dayjs().startOf('month').toISOString(),
      maxDate: dayjs().endOf('month').toISOString(),
      showFilter: false,
    };
  }

  state: Readonly<{
    isLoading: boolean;
    list: any[];
    allCategories: string[];
    selectedCategories: string[];
    showDone: boolean;
    showUndone: boolean;
    minDate: string;
    maxDate: string;
    showFilter: boolean;
  }>;

  async remove(e: any, expense: any) {
    e.preventDefault();
    const _message = expense.hasInstallments
      ? 'Essa despesa possui parcelas. Deseja remover todas as parcelas?'
      : 'Deseja remover essa despesa?';

    const _ok = window.confirm(_message);

    if (!_ok) {
      return;
    }

    await AdminApi.delete(`/expenses/${expense.id}`);

    this.setState({
      list: this.state.list.filter(
        (item) => item.id !== expense.id && item.parentId !== expense.id,
      ),
    });

    toast.success('Despesa removida com sucesso');
  }

  formatCurrency(val: number): string {
    const _d = new Decimal(val);
    return `R$ ${_d.toDecimalPlaces(2).toNumber().toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
    })}`;
  }

  async componentDidMount(): Promise<void> {
    const _allCat = await AdminApi.get('/categories');

    this.setState({
      allCategories: _allCat.map((c: any) => c.name),
      selectedCategories: this.props.searchParams?.get('categories')
        ? this.props.searchParams
            .get('categories')
            ?.split(',')
            .filter((c) => c)
        : [],
      showDone: this.props.searchParams?.get('showDone') === 'true',
      showUndone: this.props.searchParams?.get('showUndone') === 'true',
      minDate: this.props.searchParams?.get('minDate')
        ? this.props.searchParams.get('minDate')!
        : dayjs().startOf('month').toISOString(),
      maxDate: this.props.searchParams?.get('maxDate')
        ? this.props.searchParams.get('maxDate')!
        : dayjs().endOf('month').toISOString(),
    });

    setTimeout(async () => {
      await this.filter();
    }, 300);
  }

  async filter(): Promise<void> {
    this.setState({
      isLoading: true,
    });

    let _q = '?';

    const _cats = this.state.selectedCategories;
    const _showDone = this.state.showDone;
    const _showUndone = this.state.showUndone;

    const _minDate = this.state.minDate;
    const _maxDate = this.state.maxDate;

    _q += `showDone=${_showDone}&showUndone=${_showUndone}`;
    _q += `&minDate=${_minDate}&maxDate=${_maxDate}`;

    if (_cats && _cats.length) {
      _q += `&categories=${_cats.join(',')}&`;
    }

    const _data = await AdminApi.get(`/expenses${_q}`);

    this.props.searchParams?.set('showDone', String(_showDone));
    this.props.searchParams?.set('showUndone', String(_showUndone));
    this.props.searchParams?.set('minDate', _minDate);
    this.props.searchParams?.set('maxDate', _maxDate);
    this.props.searchParams?.set('categories', _cats.join(','));

    this.props.navigate({
      pathname: '/expenses',
      search: this.props.searchParams?.toString(),
    });

    this.setState({
      isLoading: false,
      list: _data,
    });
  }

  formatCategory(category: string): ReactNode {
    if (!category)
      return (
        <span key={category} className="badge bg-secondary me-1">
          Sem categoria
        </span>
      );
    return (
      <span key={category} className="badge bg-info me-1">
        {category}
      </span>
    );
  }

  async selectCategory(category: string) {
    const selected = this.state.selectedCategories.includes(category);

    const newSelected = selected
      ? this.state.selectedCategories.filter((c) => c !== category)
      : [...this.state.selectedCategories, category];

    this.setState({
      selectedCategories: newSelected,
    });
  }

  putActive(u: string) {
    return this.state.selectedCategories.includes(u) ? 'active' : '';
  }

  categoriesButtons(): ReactNode {
    return (
      <div className="row">
        <div className="col-md-12">
          {this.state.allCategories.map((u) => (
            <button
              key={u}
              type="button"
              className={`btn btn-sm btn-outline-info me-2 ${this.putActive(u)}`}
              onClick={() => this.selectCategory(u)}
            >
              {u}
            </button>
          ))}
        </div>
      </div>
    );
  }

  async setShowDone() {
    this.setState({
      showDone: !this.state.showDone,
    });
  }

  async setShowUndone() {
    this.setState({
      showUndone: !this.state.showUndone,
    });
  }

  statusesButtons(): ReactNode {
    return (
      <div className="row">
        <div className="col-md-12">
          <button
            type="button"
            className={`btn btn-sm btn-outline-primary me-2 ${this.state.showDone ? 'active' : ''}`}
            onClick={() => this.setShowDone()}
          >
            Pagas
          </button>
          <button
            type="button"
            className={`btn btn-sm btn-outline-danger me-2 ${this.state.showUndone ? 'active' : ''}`}
            onClick={() => this.setShowUndone()}
          >
            Devendo
          </button>
        </div>
      </div>
    );
  }

  dateFilter(): ReactNode {
    return (
      <div className="row ">
        <div className="col-md-3">
          <label className="form-label">Data inicial</label>
          <DatePicker
            className="form-control"
            format="dd/MM/yyyy"
            clearIcon={null}
            onChange={(d) => {
              this.setState({
                minDate: ((d ?? new Date()) as Date).toISOString(),
              });
            }}
            value={this.state.minDate}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Data final</label>
          <DatePicker
            className="form-control"
            format="dd/MM/yyyy"
            clearIcon={null}
            onChange={(d) => {
              this.setState({
                maxDate: ((d ?? new Date()) as Date).toISOString(),
              });
            }}
            value={this.state.maxDate}
          />
        </div>
      </div>
    );
  }

  filterSummary(): string {
    const _cats = this.state.selectedCategories;
    const _showDone = this.state.showDone;
    const _showUndone = this.state.showUndone;

    let summary = '';

    if (_cats && _cats.length) {
      summary += `Categorias: ${_cats.join(', ')}; `;
    }

    if (_showDone && !_showUndone) {
      summary += 'Exibir apenas despesas pagas; ';
    } else if (!_showDone && _showUndone) {
      summary += 'Exibir apenas despesas devedoras; ';
    }

    if (this.state.minDate || this.state.maxDate) {
      summary += `Período: ${dayjs(this.state.minDate).format('DD/MM/YYYY')} - ${dayjs(this.state.maxDate).format('DD/MM/YYYY')}`;
    }

    return summary || 'Nenhum filtro aplicado';
  }

  render(): ReactNode {
    return this.state.isLoading ? (
      <PageLoading />
    ) : (
      <div>
        <Header
          navigate={this.props.navigate}
          currentUser={this.props.currentUser}
        ></Header>
        <div className="container-fluid">
          <div className="row">
            <Sidebar
              navigate={this.props.navigate}
              currentUser={this.props.currentUser}
            ></Sidebar>
            <main className="ms-sm-auto large-calendar px-md-4">
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
                <h1 className="h2">Despesas</h1>
                <div className="btn-toolbar mb-2 mb-md-0">
                  <div className="btn-group me-2">
                    <NavLink
                      to="/expenses/new"
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                    >
                      Adicionar despesa
                    </NavLink>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">
                    {' '}
                    <a
                      href="#"
                      className="me-3"
                      onClick={(e) => {
                        e.preventDefault();

                        this.setState({
                          showFilter: !this.state.showFilter,
                        });
                      }}
                    >
                      <i
                        className={`bi ${this.state.showFilter ? 'bi-dash' : 'bi-window-plus'}`}
                      ></i>
                    </a>
                    Filtros{' '}
                    <small style={{ fontSize: 'small' }}>
                      ({this.filterSummary()})
                    </small>
                  </h5>
                </div>
                {this.state.showFilter && (
                  <div className="card-body">
                    {this.statusesButtons()} <hr />
                    {this.categoriesButtons()} <hr />
                    {this.dateFilter()} <hr />
                    <button
                      type="button"
                      className="btn btn-primary pull-right"
                      onClick={() => this.filter()}
                    >
                      Filtrar
                    </button>
                  </div>
                )}
              </div>
              {!this.state.list.length ? (
                <div className="row mt-3">
                  <div className="col-md-12">
                    <div className="card">
                      <div className="card-body">
                        <h5 className="card-title">
                          Nenhum despesa encontrada nessa busca
                        </h5>
                        <h6 className="card-subtitle mb-2 text-muted">
                          Modifique o filtro para encontrar as despesas.
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="table-responsive mt-3">
                  <table className="table table-striped table-hover table-bordered">
                    <thead>
                      <tr>
                        <th className="th-sm">Data</th>
                        <th>Valor</th>
                        <th>Descrição</th>
                        <th>Categoria</th>
                        <th>Paga?</th>
                        <th></th>
                      </tr>
                    </thead>

                    <tbody>
                      {this.state.list.map((item) => (
                        <tr key={item.id}>
                          <td>
                            {item.date
                              ? dayjs(item.date).format('DD/MM/YYYY')
                              : 'Sem data'}
                          </td>
                          <td>{this.formatCurrency(item.value)}</td>
                          <td>{item.description}</td>
                          <td>
                            {item.category
                              ? item.category
                                  .split(',')
                                  .map((c: string) => this.formatCategory(c))
                              : ''}
                          </td>
                          <td>
                            {item.isDone ? (
                              <span className="badge bg-primary">Paga</span>
                            ) : (
                              <span className="badge bg-danger">Devedor</span>
                            )}
                          </td>
                          <td>
                            <a href={`/admin/expenses/${item.id}/edit`}>
                              Editar
                            </a>
                            {item.installmentNumber > 1 ? (
                              <span style={{ marginLeft: '1rem' }}>
                                Não é possível remover parcelas. Remova a
                                parcela principal para remover toda a despesa.
                              </span>
                            ) : (
                              <a
                                style={{ marginLeft: '1rem' }}
                                href="#"
                                onClick={(e: any) => this.remove(e, item)}
                              >
                                Remover
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th></th>
                        <th style={{ padding: '.5rem' }}>
                          {this.formatCurrency(
                            this.state.list.reduce(
                              (acc, item) => acc + item.value,
                              0,
                            ),
                          )}
                        </th>
                        <th colSpan={4}></th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    );
  }
}
